import type { StoreCustomer } from "@medusajs/types";
import { defineStore } from "pinia";
import { useCartStore } from "~/features/cart/model";
import { useCheckoutStore } from "~/features/checkout/model";
import { getSmartCaptchaHeaders } from "~/shared/lib/yandex-smartcaptcha";

export type LoginCredentials = {
  email: string;
  password: string;
};

export type RegistrationCredentials = LoginCredentials & {
  firstName: string;
};

export type ResetPasswordCredentials = {
  email: string;
  code: string;
  password: string;
};

let restoreSessionPromise: Promise<StoreCustomer | null> | null = null;

function getErrorMessage(error: unknown) {
  const message = error instanceof Error ? error.message : "";

  if (/customer with this email already has an account/i.test(message)) {
    return "Пользователь с таким email уже зарегистрирован. Войдите в аккаунт.";
  }

  if (message) return message;

  return "Не удалось выполнить запрос. Попробуйте ещё раз.";
}

export const useAuthStore = defineStore("auth", {
  state: () => ({
    customer: null as StoreCustomer | null,
    isOpen: false,
    isCheckingSession: false,
    isSubmitting: false,
    errorMessage: null as string | null,
  }),
  getters: {
    isAuthenticated: (state) => Boolean(state.customer),
  },
  actions: {
    setCustomer(customer: StoreCustomer | null) {
      this.customer = customer;

      if (customer) {
        useCheckoutStore().setRecipientFromCustomer({
          email: customer.email,
          firstName: customer.first_name ?? "",
          lastName: customer.last_name ?? "",
          phone: customer.phone ?? "",
        });
      }
    },
    async restoreSession() {
      if (restoreSessionPromise) return restoreSessionPromise;

      this.isCheckingSession = true;
      restoreSessionPromise = (async () => {
        try {
          const { customer } =
            await useMedusaClient().store.customer.retrieve();
          this.setCustomer(customer);
          await useCartStore().transferCartToCustomer();
          return customer;
        } catch {
          // A missing or expired token is an unauthenticated state, not an app error.
          await useMedusaClient().client.clearToken();
          this.setCustomer(null);
          return null;
        } finally {
          this.isCheckingSession = false;
          restoreSessionPromise = null;
        }
      })();

      return restoreSessionPromise;
    },
    async open() {
      this.errorMessage = null;
      const customer = await this.restoreSession();
      this.isOpen = !customer;
    },
    close() {
      this.isOpen = false;
      this.errorMessage = null;
    },
    async login(credentials: LoginCredentials, captchaToken = "") {
      this.errorMessage = null;
      this.isSubmitting = true;

      try {
        const client = useMedusaClient();
        const result = await client.client.fetch<{ token?: string }>(
          "/auth/customer/emailpass",
          {
            method: "POST",
            body: credentials,
            headers: getSmartCaptchaHeaders(captchaToken),
          },
        );

        if (!result.token) throw new Error("Не удалось войти в аккаунт.");
        client.client.setToken(result.token);

        const customer = await this.restoreSession();
        if (!customer) throw new Error("Не удалось получить данные профиля.");

        this.close();
      } catch (error) {
        this.errorMessage = getErrorMessage(error);
        throw error;
      } finally {
        this.isSubmitting = false;
      }
    },
    async register(credentials: RegistrationCredentials, captchaToken = "") {
      this.errorMessage = null;
      this.isSubmitting = true;

      try {
        const client = useMedusaClient();
        const { token: registrationToken } = await client.client.fetch<{
          token?: string;
        }>("/auth/customer/emailpass/register", {
          method: "POST",
          body: {
            email: credentials.email,
            password: credentials.password,
          },
          headers: getSmartCaptchaHeaders(captchaToken),
        });
        if (!registrationToken) {
          throw new Error("Не удалось зарегистрировать аккаунт.");
        }
        client.client.setToken(registrationToken);

        await client.store.customer.create(
          {
            email: credentials.email,
            first_name: credentials.firstName,
          },
          {},
          { Authorization: `Bearer ${registrationToken}` },
        );

        await client.auth.refresh();
        const customer = await this.restoreSession();
        if (!customer) throw new Error("Не удалось получить данные профиля.");

        this.close();
      } catch (error) {
        this.errorMessage = getErrorMessage(error);
        throw error;
      } finally {
        this.isSubmitting = false;
      }
    },
    async requestPasswordReset(email: string, captchaToken = "") {
      this.errorMessage = null;
      this.isSubmitting = true;

      try {
        await useMedusaClient().client.fetch(
          "/auth/customer/emailpass/reset-password",
          {
            method: "POST",
            body: { identifier: email },
            headers: {
              accept: "text/plain",
              ...getSmartCaptchaHeaders(captchaToken),
            },
          },
        );
      } catch (error) {
        this.errorMessage = getErrorMessage(error);
        throw error;
      } finally {
        this.isSubmitting = false;
      }
    },
    async confirmPasswordReset(
      credentials: ResetPasswordCredentials,
      captchaToken = "",
    ) {
      this.errorMessage = null;
      this.isSubmitting = true;

      try {
        await useMedusaClient().client.fetch(
          "/store/auth/password-reset/confirm",
          {
            method: "POST",
            body: credentials,
            headers: getSmartCaptchaHeaders(captchaToken),
          },
        );
      } catch (error) {
        this.errorMessage = getErrorMessage(error);
        throw error;
      } finally {
        this.isSubmitting = false;
      }
    },
    async startYandexLogin() {
      this.errorMessage = null;
      this.isSubmitting = true;

      try {
        const { yandexAuthProvider } = useRuntimeConfig().public;
        const result = await useMedusaClient().auth.login(
          "customer",
          yandexAuthProvider as string,
          {},
        );

        if (typeof result === "string") {
          throw new Error("Провайдер Яндекс ID вернул некорректный ответ.");
        }

        await navigateTo(result.location, { external: true });
      } catch (error) {
        this.errorMessage = getErrorMessage(error);
      } finally {
        this.isSubmitting = false;
      }
    },
    async logout() {
      await useMedusaClient().client.clearToken();
      this.setCustomer(null);
    },
  },
});
